import { ChangeEvent, useEffect, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraGridItem,
  ChakraHStack,
  ChakraSimpleGrid,
  ChakraText,
  CircularProgress,
  EquipUITheme,
  TextInput,
} from '@equip.health/ui';

import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useAddObservationMutation } from '~/store/services/clinicalData';

import ActionButton from './ActionButton';
import DatetimeGridItem from './DatetimeGridItem';
import GoalStatusGridItem from './GoalStatusGridItem';
import MultiValueGridItem from './MultiValueGridItem';
import PositionGridItem from './PositionGridItem';
import ValueGridItem from './ValueGridItem';
import {
  BLOOD_PRESSURE,
  HEART_RATE,
  ObservationStatusType,
  PatientVitalsTabs,
  PatientVitalsTabsCollection,
  keyVitalsItemStyles,
} from '../../constants';

const TOP_LEVEL_ID = 'patient-vitals__add-value';

const { label: labelStyle, paragraph } = EquipUITheme.typography;

type NewKeyVitalsItemProps = {
  finishAddingValue: () => void;
  patientId: string;
};

const NewBloodPressureHeartRateKeyVitalsItem = ({
  finishAddingValue,
  patientId,
}: NewKeyVitalsItemProps) => {
  const [bloodPressureNumerator, setBloodPressureNumerator] = useState<
    string | undefined
  >();
  const [bloodPressureDenominator, setBloodPressureDenominator] = useState<
    string | undefined
  >();
  const [heartRate, setHeartRate] = useState<string | undefined>();
  const [interpretation, setInterpretation] = useState<string | undefined>();
  const [position, setPosition] = useState<string | null>(null);
  const [datetime, setDatetime] = useState<Date>(new Date());
  const [noteValue, setNoteValue] = useState<string | undefined>();

  const { unit } =
    PatientVitalsTabsCollection[PatientVitalsTabs.HEART_RATE_BLOOD_PRESSURE];

  const { currentUser } = useGlobalContext();

  const [
    saveObservation,
    {
      isError: saveObservationError,
      isLoading: isSaveObservationLoading,
      isSuccess: isSaveObservationSuccess,
    },
  ] = useAddObservationMutation();

  const handleSave = () => {
    const commonOptions = {
      createdBy: `${currentUser.firstName} ${currentUser.lastName}`,
      interpretation,
      note: noteValue,
      observationDatetime: datetime.toISOString(),
      position,
      secondaryValue: null,
      source: 'Manual',
      status: ObservationStatusType.FINAL,
      updatedByUserId: currentUser.userId,
    };

    const observationOptions = [];
    // Multiple observations
    if (bloodPressureNumerator && bloodPressureDenominator) {
      // Blood Pressure
      const bpOptions = {
        ...commonOptions,
        secondaryValue: Number(bloodPressureDenominator),
        type: BLOOD_PRESSURE,
        unit: unit[0],
        value: Number(bloodPressureNumerator),
      };

      observationOptions.push(bpOptions);
    }

    if (heartRate) {
      // Heart rate
      const hrOptions = {
        ...commonOptions,
        type: HEART_RATE,
        unit: unit[1],
        value: Number(heartRate),
      };

      observationOptions.push(hrOptions);
    }

    // Save single/multiple observations
    observationOptions.forEach((option) => {
      saveObservation({ ...option, patientId });
    });
  };

  useEffect(() => {
    if (isSaveObservationSuccess) {
      finishAddingValue();
    }
  }, [isSaveObservationSuccess]);

  return (
    <ChakraBox {...keyVitalsItemStyles.container} id={TOP_LEVEL_ID}>
      <ChakraSimpleGrid
        {...keyVitalsItemStyles.mainGrid}
        templateColumns="15% 8% 25% 12% 8% 10% auto"
      >
        <MultiValueGridItem
          colSpan={1}
          id={`${TOP_LEVEL_ID}__multi-value`}
          isEditing
          onChange={(val: string[]) => {
            setBloodPressureNumerator(val[0]?.replaceAll('.', ''));
            setBloodPressureDenominator(val[1]?.replaceAll('.', ''));
          }}
          unit={unit[0]}
          value={[bloodPressureNumerator, bloodPressureDenominator]}
        />
        <ValueGridItem<string>
          colSpan={1}
          id={`${TOP_LEVEL_ID}__second-value`}
          isEditing
          onChange={(val) => {
            setHeartRate(val.replaceAll('.', ''));
          }}
          tab={PatientVitalsTabs.HEART_RATE_BLOOD_PRESSURE}
          unit={unit[1]}
          value={heartRate}
        />
        <DatetimeGridItem
          colSpan={1}
          id={`${TOP_LEVEL_ID}__measured-at`}
          isEditing
          onChange={(val: Date) => setDatetime(val)}
          value={datetime}
        />
        <PositionGridItem
          colSpan={1}
          id={`${TOP_LEVEL_ID}__position`}
          isEditing
          onChange={(val: string) => setPosition(val)}
          value={position}
        />
        <ChakraGridItem alignItems="center" colSpan={1}>
          <ChakraText {...paragraph} id={`${TOP_LEVEL_ID}__source`}>
            Manual
          </ChakraText>
        </ChakraGridItem>
        <GoalStatusGridItem
          colSpan={1}
          id={`${TOP_LEVEL_ID}__goal-status`}
          isEditing
          onChange={(val: string) => setInterpretation(val)}
          value={interpretation}
        />
        <ChakraGridItem colSpan={1}>
          <ChakraFlex justify="flex-end">
            {isSaveObservationLoading ? (
              <CircularProgress
                id={`${TOP_LEVEL_ID}__loading`}
                isIndeterminate
                size="20px"
              />
            ) : (
              <ChakraHStack spacing="4px">
                <ActionButton
                  id={`${TOP_LEVEL_ID}__save`}
                  isDisabled={
                    !datetime ||
                    !position ||
                    (!(bloodPressureNumerator && bloodPressureDenominator) &&
                      !heartRate)
                  }
                  onClick={handleSave}
                  title="Save"
                  variant="solid"
                />
                <ActionButton
                  id={`${TOP_LEVEL_ID}__cancel`}
                  onClick={finishAddingValue}
                  title="Cancel"
                  variant="outline"
                />
              </ChakraHStack>
            )}
          </ChakraFlex>
        </ChakraGridItem>
      </ChakraSimpleGrid>
      <ChakraSimpleGrid
        gap="24px"
        id={`${TOP_LEVEL_ID}__note-grid`}
        paddingTop="16px"
      >
        <ChakraText
          {...labelStyle}
          id={`${TOP_LEVEL_ID}__note-label`}
          lineHeight="120%"
        >
          Note
        </ChakraText>
        <TextInput
          color="neutral.primary"
          formMarginBottom="10px"
          height="32px"
          id={`${TOP_LEVEL_ID}__note-input`}
          isRequired
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setNoteValue(event.target.value);
          }}
          size="sm"
          value={noteValue}
        />
      </ChakraSimpleGrid>
      {saveObservationError && (
        <ChakraText {...labelStyle} color="critical.base" textAlign="right">
          Error saving observation
        </ChakraText>
      )}
    </ChakraBox>
  );
};

export default NewBloodPressureHeartRateKeyVitalsItem;
